import React from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ChevronDown, Eye } from 'lucide-react';
import { BankSearchResult } from '../../../../utils/types/candi_bank_types';

export interface UnverifiedDrawerProps {
  unverifiedCount: number;
  expanded: boolean;
  onToggle: () => void;
  unverifiedResults: BankSearchResult[];
  onViewProfile: (result: BankSearchResult) => void;
}

export const UnverifiedDrawer: React.FC<UnverifiedDrawerProps> = ({
  unverifiedCount,
  expanded,
  onToggle,
  unverifiedResults,
  onViewProfile,
}) => {
  if (unverifiedCount <= 0) return null;

  return (
    <Box sx={{ width: '100%', my: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          variant='outlined'
          size='small'
          onClick={onToggle}
          endIcon={
            <ChevronDown
              size={15}
              style={{
                transform: expanded ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.2s',
              }}
            />
          }
          sx={{
            borderRadius: 999,
            bgcolor: 'grey.100',
            borderColor: 'divider',
            color: 'text.secondary',
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '0.75rem',
            px: 2,
            py: 0.75,
            '&:hover': {
              bgcolor: 'grey.200',
              borderColor: 'divider',
            },
          }}
        >
          {unverifiedCount} candidates with unparseable CV dates
        </Button>
      </Box>

      {expanded && (
        <Paper
          variant='outlined'
          sx={{
            mt: 1.5,
            p: 2,
            bgcolor: 'rgba(248, 250, 252, 0.7)',
            borderColor: 'divider',
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          <TableContainer>
            <Table size='small'>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.6875rem', textTransform: 'uppercase' }}>
                    Name
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.6875rem', textTransform: 'uppercase' }}>
                    Email
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.6875rem', textTransform: 'uppercase' }}>
                    Skills
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.6875rem', textTransform: 'uppercase' }}>
                    Years
                  </TableCell>
                  <TableCell align='right' sx={{ fontWeight: 700, fontSize: '0.6875rem', textTransform: 'uppercase' }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {unverifiedResults.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align='center' sx={{ py: 3, color: 'text.disabled' }}>
                      Loading unverified candidates...
                    </TableCell>
                  </TableRow>
                ) : (
                  unverifiedResults.map((result, idx) => {
                    const skills = result.skills ? result.skills.slice(0, 5).join(', ') : 'N/A';
                    const name =
                      [result.firstName, result.lastName].filter(Boolean).join(' ') ||
                      'Unknown';

                    return (
                      <TableRow key={result.id || idx} hover>
                        <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>
                          {name}
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.6875rem', color: 'text.secondary' }}>
                          {result.email || '—'}
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>{skills}</TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>
                          {result.years !== undefined ? `${result.years}y` : '—'}
                        </TableCell>
                        <TableCell align='right'>
                          <Button
                            size='small'
                            onClick={() => onViewProfile(result)}
                            startIcon={<Eye size={14} />}
                            sx={{
                              textTransform: 'none',
                              fontWeight: 700,
                              fontSize: '0.6875rem',
                              color: 'primary.main',
                              p: 0.5,
                            }}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};
